import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, MicrophoneSlash, Microphone, SpeakerHigh, SpeakerSlash, Phone, PhoneDisconnect, UserPlus, UserCircle, MagnifyingGlass } from '@phosphor-icons/react';
import axios from 'axios';

const STUN_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' }
  ]
};

const AudioCallScreen = ({ 
  socket, 
  callId, 
  callerId, 
  calleeId, 
  callerName, 
  calleeName,
  callerProfilePic,
  calleeProfilePic,
  isIncoming,
  isConference,
  currentUserId,
  currentUserRole,
  onClose,
  API
}) => {
  const [callStatus, setCallStatus] = useState(isIncoming ? 'incoming' : 'calling');
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaker, setIsSpeaker] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  const localStream = useRef(null);
  const peersRef = useRef({}); // {userId: RTCPeerConnection}
  const remoteAudioElements = useRef({}); // {userId: HTMLAudioElement}
  const ringtoneRef = useRef(null);
  const audioCtxRef = useRef(null);
  const durationInterval = useRef(null);
  const callIdRef = useRef(callId);
  const audioContainerRef = useRef(null);
  const pendingIceCandidates = useRef({}); // {userId: [candidates]} - queue for early ICE candidates

  const otherName = currentUserId === callerId ? calleeName : callerName;
  const otherPic = currentUserId === callerId ? calleeProfilePic : callerProfilePic;
  const otherUserId = currentUserId === callerId ? calleeId : callerId;
  
  // Can this user add people to the call?
  const canAddUsers = currentUserRole !== 'customer';

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Play ringtone
  const playRingtone = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      const playTone = () => {
        if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') return;
        try {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.value = 440;
          gain.gain.value = 0.15;
          osc.start();
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
          osc.stop(ctx.currentTime + 0.6);
        } catch(e) {}
      };
      playTone();
      ringtoneRef.current = setInterval(playTone, 2500);
    } catch (e) {
      console.warn('Ringtone failed:', e);
    }
  }, []);

  const stopRingtone = useCallback(() => {
    if (ringtoneRef.current) {
      clearInterval(ringtoneRef.current);
      ringtoneRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch(e) {}
      audioCtxRef.current = null;
    }
  }, []);

  // Create a peer connection for a specific user
  const createPeerConnection = useCallback((targetUserId) => {
    if (peersRef.current[targetUserId]) {
      try { peersRef.current[targetUserId].close(); } catch(e) {}
    }

    const pc = new RTCPeerConnection(STUN_SERVERS);
    peersRef.current[targetUserId] = pc;

    // Add local audio tracks
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => pc.addTrack(track, localStream.current));
    }

    // Process any queued ICE candidates for this peer
    if (pendingIceCandidates.current[targetUserId]?.length > 0) {
      const pending = pendingIceCandidates.current[targetUserId];
      pendingIceCandidates.current[targetUserId] = [];
      pending.forEach(async (candidate) => {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch(e) {}
      });
    }

    // Handle remote audio
    pc.ontrack = (event) => {
      const remoteStream = event.streams[0];
      // Create or reuse audio element for this user
      if (!remoteAudioElements.current[targetUserId]) {
        const audioEl = new Audio();
        audioEl.autoplay = true;
        audioEl.playsInline = true;
        remoteAudioElements.current[targetUserId] = audioEl;
      }
      remoteAudioElements.current[targetUserId].srcObject = remoteStream;
    };

    // ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && socket) {
        socket.emit('webrtc_ice_candidate', {
          candidate: event.candidate,
          target_id: targetUserId,
          call_id: callIdRef.current
        });
      }
    };

    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') {
        // At least one peer connected
        setCallStatus('connected');
        stopRingtone();
      }
    };

    return pc;
  }, [socket, stopRingtone]);

  // Get local audio stream
  const getLocalStream = useCallback(async () => {
    if (localStream.current) return localStream.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      localStream.current = stream;
      return stream;
    } catch (e) {
      console.error('Failed to get audio:', e);
      return null;
    }
  }, []);

  // Initiate call (caller side)
  const initiateCall = useCallback(async () => {
    const stream = await getLocalStream();
    if (!stream) {
      setCallStatus('ended');
      return;
    }

    const pc = createPeerConnection(calleeId);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    
    socket.emit('webrtc_offer', {
      offer: offer,
      caller_id: callerId,
      target_id: calleeId,
      call_id: callIdRef.current
    });
    
    setCallStatus('ringing');
    playRingtone();

    // Set initial participants
    setParticipants([
      { id: callerId, name: callerName },
      { id: calleeId, name: calleeName }
    ]);
  }, [getLocalStream, createPeerConnection, socket, callerId, calleeId, callerName, calleeName, playRingtone]);

  // Accept call (callee side)
  const acceptCall = useCallback(async () => {
    stopRingtone();
    setCallStatus('connecting');
    
    if (isConference) {
      // Conference call - emit conference_accept
      socket.emit('conference_accept', {
        call_id: callIdRef.current,
        user_id: currentUserId,
        user_name: currentUserId === calleeId ? calleeName : callerName
      });
    } else {
      // 1:1 call
      socket.emit('call_accept', {
        call_id: callIdRef.current,
        callee_id: currentUserId
      });
    }

    await getLocalStream();
    
    setParticipants([
      { id: callerId, name: callerName },
      { id: calleeId, name: calleeName }
    ]);
  }, [getLocalStream, socket, currentUserId, callerId, calleeId, callerName, calleeName, stopRingtone, isConference]);

  // Handle WebRTC signaling events
  useEffect(() => {
    if (!socket) return;
    
    const handleOffer = async (data) => {
      if (data.call_id !== callIdRef.current) return;
      const fromUserId = data.caller_id || data.from_id;
      
      await getLocalStream();
      const pc = createPeerConnection(fromUserId);
      
      await pc.setRemoteDescription(new RTCSessionDescription(data.offer));
      
      // Process any queued ICE candidates now that remote description is set
      if (pendingIceCandidates.current[fromUserId]?.length > 0) {
        const pending = pendingIceCandidates.current[fromUserId];
        pendingIceCandidates.current[fromUserId] = [];
        for (const candidate of pending) {
          try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); } catch(e) {}
        }
      }
      
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      
      socket.emit('webrtc_answer', {
        answer: answer,
        target_id: fromUserId,
        call_id: callIdRef.current
      });
    };
    
    const handleAnswer = async (data) => {
      if (data.call_id !== callIdRef.current) return;
      const fromUserId = data.from_id || otherUserId;
      const pc = peersRef.current[fromUserId];
      if (pc && pc.signalingState !== 'stable') {
        await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
      }
    };
    
    const handleIceCandidate = async (data) => {
      if (data.call_id !== callIdRef.current) return;
      const fromUserId = data.from_id || otherUserId;
      const pc = peersRef.current[fromUserId];
      if (pc && data.candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } catch (e) {
          console.warn('ICE candidate error:', e);
        }
      } else if (data.candidate) {
        // Queue ICE candidates for when PC is created
        if (!pendingIceCandidates.current[fromUserId]) {
          pendingIceCandidates.current[fromUserId] = [];
        }
        pendingIceCandidates.current[fromUserId].push(data.candidate);
      }
    };
    
    const handleCallEnded = (data) => {
      if (data.user_id) {
        // A specific user left the conference
        closePeerForUser(data.user_id);
        setParticipants(prev => prev.filter(p => p.id !== data.user_id));
        
        // If only 1 participant left (just me), end call
        setParticipants(prev => {
          if (prev.length <= 1) {
            handleEndCall();
          }
          return prev;
        });
      } else {
        // Full call ended
        stopRingtone();
        setCallStatus('ended');
        setTimeout(() => onClose(), 2000);
      }
    };
    
    const handleCallRejected = () => {
      stopRingtone();
      setCallStatus('ended');
      setTimeout(() => onClose(), 2000);
    };
    
    const handleCallAccepted = async (data) => {
      stopRingtone();
      setCallStatus('connecting');
      
      // Wait for callee's AudioCallScreen to mount and register WebRTC handlers
      setTimeout(async () => {
        try {
          await getLocalStream();
          const pc = createPeerConnection(otherUserId);
          const offer = await pc.createOffer();
          await pc.setLocalDescription(offer);
          socket.emit('webrtc_offer', {
            offer: offer,
            caller_id: currentUserId,
            from_id: currentUserId,
            target_id: otherUserId,
            call_id: callIdRef.current
          });
        } catch(e) {
          console.error('Error re-sending offer:', e);
        }
      }, 800);
    };

    // Conference: new participant joined
    const handleConferenceJoined = async (data) => {
      if (data.call_id !== callIdRef.current) return;
      const newUserId = data.user_id;
      const newUserName = data.user_name;
      
      // Add to participants
      setParticipants(prev => {
        if (prev.find(p => p.id === newUserId)) return prev;
        return [...prev, { id: newUserId, name: newUserName }];
      });

      // Create offer for the new participant
      await getLocalStream();
      const pc = createPeerConnection(newUserId);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      socket.emit('webrtc_offer', {
        offer: offer,
        caller_id: currentUserId,
        from_id: currentUserId,
        target_id: newUserId,
        call_id: callIdRef.current
      });
    };

    // Conference: user left
    const handleConferenceLeft = (data) => {
      if (data.call_id !== callIdRef.current) return;
      closePeerForUser(data.user_id);
      setParticipants(prev => prev.filter(p => p.id !== data.user_id));
    };
    
    socket.on('webrtc_offer', handleOffer);
    socket.on('webrtc_answer', handleAnswer);
    socket.on('webrtc_ice_candidate', handleIceCandidate);
    socket.on('call_ended', handleCallEnded);
    socket.on('call_rejected', handleCallRejected);
    socket.on('call_accepted', handleCallAccepted);
    socket.on('conference_user_joined', handleConferenceJoined);
    socket.on('conference_user_left', handleConferenceLeft);
    
    return () => {
      socket.off('webrtc_offer', handleOffer);
      socket.off('webrtc_answer', handleAnswer);
      socket.off('webrtc_ice_candidate', handleIceCandidate);
      socket.off('call_ended', handleCallEnded);
      socket.off('call_rejected', handleCallRejected);
      socket.off('call_accepted', handleCallAccepted);
      socket.off('conference_user_joined', handleConferenceJoined);
      socket.off('conference_user_left', handleConferenceLeft);
    };
  }, [socket, onClose, otherUserId, currentUserId, getLocalStream, createPeerConnection, stopRingtone]);

  // Auto-initiate for caller
  useEffect(() => {
    if (!isIncoming) {
      initiateCall();
    } else {
      playRingtone();
      setParticipants([
        { id: callerId, name: callerName },
        { id: calleeId, name: calleeName }
      ]);
    }
    
    return () => {
      stopRingtone();
      if (durationInterval.current) clearInterval(durationInterval.current);
      // Cleanup all peer connections
      Object.values(peersRef.current).forEach(pc => {
        try { pc.close(); } catch(e) {}
      });
      peersRef.current = {};
      // Cleanup audio elements
      Object.values(remoteAudioElements.current).forEach(el => {
        try { el.srcObject = null; } catch(e) {}
      });
      remoteAudioElements.current = {};
      // Stop local stream
      if (localStream.current) {
        localStream.current.getTracks().forEach(t => t.stop());
        localStream.current = null;
      }
    };
  }, []);

  // Duration timer
  useEffect(() => {
    if (callStatus === 'connected') {
      durationInterval.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (durationInterval.current) clearInterval(durationInterval.current);
    };
  }, [callStatus]);

  // Close peer connection for a specific user
  const closePeerForUser = (userId) => {
    if (peersRef.current[userId]) {
      try { peersRef.current[userId].close(); } catch(e) {}
      delete peersRef.current[userId];
    }
    if (remoteAudioElements.current[userId]) {
      try { remoteAudioElements.current[userId].srcObject = null; } catch(e) {}
      delete remoteAudioElements.current[userId];
    }
  };

  const handleEndCall = useCallback(() => {
    stopRingtone();
    if (socket) {
      socket.emit('call_end', { call_id: callIdRef.current, user_id: currentUserId });
    }
    // Cleanup all peers
    Object.values(peersRef.current).forEach(pc => {
      try { pc.close(); } catch(e) {}
    });
    peersRef.current = {};
    Object.values(remoteAudioElements.current).forEach(el => {
      try { el.srcObject = null; } catch(e) {}
    });
    remoteAudioElements.current = {};
    if (localStream.current) {
      localStream.current.getTracks().forEach(t => t.stop());
      localStream.current = null;
    }
    setCallStatus('ended');
    setTimeout(() => onClose(), 1500);
  }, [socket, currentUserId, onClose, stopRingtone]);

  const handleRejectCall = () => {
    stopRingtone();
    if (socket) {
      socket.emit('call_reject', { call_id: callIdRef.current });
    }
    setCallStatus('ended');
    setTimeout(() => onClose(), 1000);
  };

  const toggleMute = () => {
    if (localStream.current) {
      const audioTrack = localStream.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  };

  const toggleSpeaker = () => {
    const newSpeakerState = !isSpeaker;
    setIsSpeaker(newSpeakerState);
    // Toggle volume on all remote audio elements
    Object.values(remoteAudioElements.current).forEach(audioEl => {
      if (audioEl) {
        audioEl.volume = newSpeakerState ? 1.0 : 0.4;
      }
    });
  };

  // Fetch available users for Add Call
  const fetchAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const [staffRes, customerRes] = await Promise.all([
        axios.get(`${API}/users/all-staff`),
        axios.get(`${API}/users/all-customers`)
      ]);
      const allUsers = [...(staffRes.data || []), ...(customerRes.data || [])];
      // Filter out current participants and self
      const participantIds = new Set(participants.map(p => p.id));
      participantIds.add(currentUserId);
      const available = allUsers.filter(u => !participantIds.has(u.id));
      setAvailableUsers(available);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Add user to conference call
  const addUserToCall = (targetUser) => {
    if (participants.length >= 10) {
      return; // Max 10 participants
    }
    socket.emit('conference_add_user', {
      call_id: callIdRef.current,
      adder_id: currentUserId,
      adder_name: currentUserId === callerId ? callerName : calleeName,
      target_id: targetUser.id,
      target_name: targetUser.username
    });
    setShowAddModal(false);
    setSearchQuery('');
  };

  const filteredUsers = availableUsers.filter(u => 
    u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.phone?.includes(searchQuery)
  );

  const getRoleBadge = (role) => {
    if (role === 'super_admin') return { text: 'Admin', color: 'bg-wa-hover text-wa-text-primary' };
    if (role === 'deposit_handler') return { text: 'Deposit', color: 'bg-wa-hover text-wa-text-primary' };
    if (role === 'withdrawal_handler') return { text: 'Withdrawal', color: 'bg-wa-hover text-wa-text-primary' };
    if (role === 'support_handler') return { text: 'Support', color: 'bg-wa-hover text-wa-text-primary' };
    if (role === 'inquiry_handler') return { text: 'Inquiry', color: 'bg-wa-hover text-wa-text-primary' };
    return { text: 'Customer', color: 'bg-gray-100 text-gray-700' };
  };

  const statusText = {
    calling: 'Calling...',
    incoming: 'Incoming Call',
    ringing: 'Ringing...',
    connecting: 'Connecting...',
    connected: formatDuration(duration),
    ended: 'Call Ended'
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: 'linear-gradient(180deg, #1a3a2a 0%, #0d1f17 50%, #0a1a12 100%)' }} data-testid="audio-call-screen">
      {/* Top bar */}
      <div className="flex items-center justify-between p-4 pt-8">
        <button onClick={handleEndCall} className="p-2 text-white/60 hover:text-white" data-testid="close-call-btn">
          <X size={24} weight="bold" />
        </button>
        <div className="flex items-center gap-3">
          {canAddUsers && callStatus === 'connected' && participants.length < 10 && (
            <button 
              onClick={() => { setShowAddModal(true); fetchAvailableUsers(); }}
              className="p-2 text-white/60 hover:text-white"
              title="Add to call"
              data-testid="add-call-btn"
            >
              <UserPlus size={22} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Profile & Name */}
      <div className="flex-1 flex flex-col items-center justify-center px-8">
        {/* Profile Picture(s) */}
        {participants.length <= 2 ? (
          // 1:1 call - show single large avatar
          <div className="w-32 h-32 rounded-full overflow-hidden mb-6 shadow-2xl ring-4 ring-white/10">
            {otherPic ? (
              <img src={otherPic.startsWith('http') ? otherPic : `${API}${otherPic}`} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#00a884] to-[#008069] flex items-center justify-center">
                <span className="text-5xl font-bold text-white">{(otherName || 'U')[0]?.toUpperCase()}</span>
              </div>
            )}
          </div>
        ) : (
          // Conference call - show participant grid
          <div className="flex flex-wrap justify-center gap-3 mb-6 max-w-[280px]">
            {participants.filter(p => p.id !== currentUserId).map(p => (
              <div key={p.id} className="flex flex-col items-center gap-1">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] flex items-center justify-center ring-2 ring-white/20">
                  <span className="text-xl font-bold text-white">{(p.name || 'U')[0]?.toUpperCase()}</span>
                </div>
                <span className="text-[10px] text-white/60 max-w-[60px] truncate">{p.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* Name */}
        <h2 className="text-2xl font-bold text-white mb-2">
          {participants.length <= 2 ? (otherName || 'Unknown') : `Conference (${participants.length})`}
        </h2>
        
        {/* Status */}
        <p className={`text-sm ${callStatus === 'connected' ? 'text-[#00a884]' : 'text-white/60'}`}>
          {statusText[callStatus] || 'Unknown'}
        </p>

        {/* Encryption notice */}
        <p className="text-[11px] text-white/30 mt-8 flex items-center gap-1">
          <svg width="12" height="12" fill="currentColor" viewBox="0 0 16 16"><path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/></svg>
          End-to-end encrypted
        </p>
      </div>

      {/* Bottom Controls */}
      <div className="pb-12 px-8">
        {callStatus === 'incoming' ? (
          <div className="flex items-center justify-around">
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={handleRejectCall}
                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                data-testid="reject-call-btn"
              >
                <PhoneDisconnect size={28} weight="fill" className="text-white" />
              </button>
              <span className="text-white/70 text-xs">Decline</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={acceptCall}
                className="w-16 h-16 rounded-full bg-[#00a884] flex items-center justify-center shadow-lg hover:bg-[#008f72] transition-colors"
                data-testid="accept-call-btn"
              >
                <Phone size={28} weight="fill" className="text-white" />
              </button>
              <span className="text-white/70 text-xs">Accept</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-around">
            {/* Speaker */}
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={toggleSpeaker}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  isSpeaker ? 'bg-white text-[#0d1f17]' : 'bg-white/15 text-white'
                }`}
                data-testid="speaker-btn"
              >
                {isSpeaker ? <SpeakerHigh size={24} weight="fill" /> : <SpeakerSlash size={24} />}
              </button>
              <span className="text-white/50 text-[10px]">Speaker</span>
            </div>

            {/* Mute */}
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={toggleMute}
                className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
                  isMuted ? 'bg-white text-[#0d1f17]' : 'bg-white/15 text-white'
                }`}
                data-testid="mute-btn"
              >
                {isMuted ? <MicrophoneSlash size={24} weight="fill" /> : <Microphone size={24} />}
              </button>
              <span className="text-white/50 text-[10px]">Mute</span>
            </div>

            {/* End Call */}
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={handleEndCall}
                className="w-16 h-16 rounded-full bg-red-500 flex items-center justify-center shadow-lg hover:bg-red-600 transition-colors"
                data-testid="end-call-btn"
              >
                <PhoneDisconnect size={28} weight="fill" className="text-white" />
              </button>
              <span className="text-white/50 text-[10px]">End</span>
            </div>
          </div>
        )}
      </div>

      {/* Add Call Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-end sm:items-center justify-center" onClick={() => setShowAddModal(false)}>
          <div className="bg-[#111b21] w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="add-call-modal">
            {/* Header */}
            <div className="p-4 border-b border-white/10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Add to Call</h3>
                <button onClick={() => setShowAddModal(false)} className="text-white/60 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              <div className="text-[11px] text-white/40 mb-3">
                {participants.length}/10 participants
              </div>
              {/* Search */}
              <div className="flex items-center bg-[#202c33] rounded-lg px-3 py-2">
                <MagnifyingGlass size={18} className="text-white/40 mr-2" />
                <input
                  type="text"
                  placeholder="Search name or phone..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="bg-transparent text-white text-sm flex-1 outline-none placeholder-white/40"
                  data-testid="add-call-search"
                />
              </div>
            </div>

            {/* User List */}
            <div className="flex-1 overflow-y-auto p-2">
              {loadingUsers ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-2 border-[#00a884] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <p className="text-center text-white/40 py-8 text-sm">No users available</p>
              ) : (
                filteredUsers.map(u => {
                  const badge = getRoleBadge(u.role);
                  return (
                    <button
                      key={u.id}
                      onClick={() => addUserToCall(u)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white/5 rounded-lg transition-colors"
                      data-testid={`add-user-${u.id}`}
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00a884] to-[#008069] flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-bold text-white">{(u.username || 'U')[0]?.toUpperCase()}</span>
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-white text-sm font-medium truncate">{u.username}</p>
                        <p className="text-white/40 text-xs">{u.phone}</p>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge.color}`}>{badge.text}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioCallScreen;
